/**
 * Dual Currency Price Updates
 * 
 * This script handles dynamic price updates when product variants change,
 * ensuring that both BGN and EUR prices are updated in real-time.
 */

class DualCurrencyManager {
  constructor() {
    this.exchangeRate = null;
    this.init();
  }

  init() {
    // Get exchange rate from metaobject
    this.getExchangeRate();
    
    // Set up variant change listeners
    this.setupVariantListeners();
  }

  getExchangeRate() {
    // Try to get exchange rate from the global metaobject
    // This would typically be set via a script tag in the theme
    const exchangeRateElement = document.querySelector('[data-exchange-rate]');
    if (exchangeRateElement) {
      this.exchangeRate = parseFloat(exchangeRateElement.dataset.exchangeRate);
    } else {
      // Fallback to default BGN to EUR rate (as of 2024)
      this.exchangeRate = 1.95583;
    }
  }

  setupVariantListeners() {
    // Listen for variant changes
    document.addEventListener('variant:change', (event) => {
      if (event.detail && event.detail.variant) {
        this.updateDualCurrencyPrices(event.detail.variant);
      }
    });

    // Listen for form changes (fallback)
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantSelect = productForm.querySelector('select[name="id"]');
      const variantInputs = productForm.querySelectorAll('input[name="id"]');

      if (variantSelect) {
        variantSelect.addEventListener('change', () => this.handleVariantChange());
      }

      variantInputs.forEach(input => {
        input.addEventListener('change', () => this.handleVariantChange());
      });
    }
  }

  handleVariantChange() {
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (!productForm) return;

    const formData = new FormData(productForm);
    const variantId = formData.get('id');
    
    if (!variantId) return;

    // Fetch variant data
    fetch(`${window.location.pathname}?variant=${variantId}&view=json`)
      .then(response => response.json())
      .then(data => {
        if (data.variant) {
          this.updateDualCurrencyPrices(data.variant);
        }
      })
      .catch(error => {
        console.error('Error fetching variant data:', error);
      });
  }

  updateDualCurrencyPrices(variant) {
    // Update all dual currency price elements
    const dualCurrencyElements = document.querySelectorAll('.dual-currency-price, .dual-currency-price-range');
    
    dualCurrencyElements.forEach(element => {
      this.updateElement(element, variant);
    });
  }

  updateElement(element, variant) {
    const isRange = element.classList.contains('dual-currency-price-range');
    
    if (isRange) {
      this.updatePriceRange(element, variant);
    } else {
      this.updateSinglePrice(element, variant);
    }
  }

  updateSinglePrice(element, variant) {
    const currentContainer = element.querySelector('.dual-currency-price__current');
    const compareContainer = element.querySelector('.dual-currency-price__compare');
    
    if (!currentContainer) return;

    const hasComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price;
    const currentEur = (variant.price / 100) / this.exchangeRate;
    const compareEur = variant.compare_at_price ? (variant.compare_at_price / 100) / this.exchangeRate : 0;

    // Update current price
    const currentBgn = currentContainer.querySelector('span:first-child');
    const currentEurSpan = currentContainer.querySelector('span:last-child');
    
    if (currentBgn) {
      currentBgn.textContent = this.formatMoneyBGN(variant.price);
    }
    if (currentEurSpan) {
      currentEurSpan.textContent = '€' + currentEur.toFixed(2);
    }

    // Update compare price if exists
    if (hasComparePrice && compareContainer) {
      const compareBgn = compareContainer.querySelector('span:first-child');
      const compareEurSpan = compareContainer.querySelector('span:last-child');
      
      if (compareBgn) {
        compareBgn.textContent = this.formatMoneyBGN(variant.compare_at_price);
      }
      if (compareEurSpan) {
        compareEurSpan.textContent = '€' + compareEur.toFixed(2);
      }
    }
  }

  updatePriceRange(element, variant) {
    // For price ranges, we might need to fetch the full product data
    // This is a simplified version - you might need to enhance this
    console.log('Price range update not fully implemented yet');
  }

  formatMoneyBGN(cents) {
    return (cents / 100).toLocaleString('bg-BG', {
      style: 'currency',
      currency: 'BGN',
      minimumFractionDigits: 2
    });
  }

  formatMoneyEUR(cents) {
    return (cents / 100).toLocaleString('en-EU', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new DualCurrencyManager();
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new DualCurrencyManager();
  });
} else {
  new DualCurrencyManager();
}
